import Select from '../../../components/Select';
import type { ReactNode } from 'react';
import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from 'react';
import { Input } from '../../../components/Input';
import { CustomDateRangePicker } from './CustomDateRangePicker';
import { Button } from '../../../components/Button';
import magnifyingGlass from '@/assets/images/common/magnifying-glass.png';
import {
    formatDateForServer,
    getInitialDate,
    createIndividualCalendarOptions,
    getDefaultIndividualCalendarOption,
    IndividualCalendarItem,
    CalendarOption,
    SearchParams,
    ColorOption,
    getDefaultColorOption,
    getBaseColorOptionsByProvider,
    applyColorOrderWithDividers,
    isHolidayOrBirthdayCalendar,
} from './util/Search';
import { getLocalStorage, setLocalStorage } from '@/utils/localStrage';
import { LocalStorageKeys } from '@/constants/localStorage';
import { useTranslation } from 'react-i18next';
import { useUser } from '@/hooks/UserContext';
import { buildLanguageTagFromUser } from '@/utils/locale';
import { resolveWeekStart } from '@/i18n/weekStart';
import Modal from '@/components/Modal';
import { FilterIcon } from '@/components/icons/FilterIcon';
import { ExternalLinkIcon } from '@/components/icons/ExternalLinkIcon';
import { PeriodLockedModal } from '@/components/PeriodLockedModal';
import { openPlansWithTracking } from '@/utils/openPlansWithTracking';

// プロップス
interface SearchProps {
    handleSearch?: (
        searchCalendar: CalendarOption,
        startTime: string,
        endTime: string,
        searchKeyword: string,
        searchColor: ColorOption,
        bucket: 'day' | 'week' | 'month',
    ) => void;
    calendarList?: IndividualCalendarItem[]; // 個別カレンダーリストに変更
    colorOptions?: ColorOption[];
    // 初期値および更新後の値保持のための追加プロパティ
    initialSearchParams?: SearchParams;
    /** true のとき期間ピッカーを非表示（既定の期間は内部状態のまま API に渡す） */
    hidePeriod?: boolean;
    /** 送信ボタン表示文言（未指定時は「分析する」等の既定翻訳） */
    submitButtonLabel?: string;
    /** true のとき送信ボタンを出さず、条件変更で自動的に handleSearch を呼ぶ（デバウンスあり） */
    hideSubmitButton?: boolean;
    /** 分析ページ: ②ビュー操作ブロック（タブ＋ページネーション等）をデータソースとフィルタの間に挿入 */
    analysisToolbarMiddle?: ReactNode;
}

interface PlanLockedModalProps {
    isOpen: boolean;
    onClose: () => void;
    onGoPlans: () => void;
}

function PlanLockedModal({ isOpen, onClose, onGoPlans }: PlanLockedModalProps) {
    const { t } = useTranslation('analysis');
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            contentClassName="w-full max-w-md p-6"
        >
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-center">
                    {t('lockedModal.title')}
                </h3>
                <div
                    className="text-sm text-gray-700 [&_strong]:font-semibold [&_strong]:block [&_strong]:mt-3 [&_strong]:mb-2 [&_ul]:list-disc [&_ul]:list-inside [&_ul]:mt-2 [&_ul]:mb-2 [&_ul]:pl-4 [&_li]:mb-1 [&_li]:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: t('lockedModal.message') }}
                />
                <div className="flex justify-center mt-4">
                    <button
                        type="button"
                        className="px-6 py-2 rounded-md bg-kotomilThemaColor text-black hover:bg-kotomilThemaColor/90 text-sm inline-flex items-center gap-2"
                        onClick={() => {
                            onClose();
                            onGoPlans();
                        }}
                    >
                        <span>{t('lockedModal.cta')}</span>
                        <ExternalLinkIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// コンポーネント
const Search = ({
    handleSearch,
    calendarList,
    initialSearchParams,
    hidePeriod = false,
    submitButtonLabel,
    hideSubmitButton = false,
    analysisToolbarMiddle,
}: SearchProps) => {
    const { t, i18n } = useTranslation('search');
    const { userInfo } = useUser();

    const localizeColorOption = useCallback(
        (option?: ColorOption | null): ColorOption => {
            if (!option) {
                const base = getDefaultColorOption();
                return { ...base, label: t('color.none') };
            }
            if (option.isDivider) {
                return option;
            }
            if (option.value === '') {
                return {
                    ...option,
                    label: t('color.none'),
                    originalLabel: option.originalLabel ?? option.label,
                };
            }
            if (option.value === 'all') {
                return {
                    ...option,
                    label: t('color.all'),
                    originalLabel: option.originalLabel ?? option.label,
                };
            }
            return {
                ...option,
                originalLabel: option.originalLabel ?? option.label,
            };
        },
        [t],
    );

    const languageTag = buildLanguageTagFromUser(userInfo, i18n.language);
    const weekStart = resolveWeekStart(userInfo?.weekStart);
    const isPaidPlan = Boolean(userInfo?.isPaid);


    // 初期キーワード
    const [searchKeyword, setSearchKeyword] = useState(initialSearchParams?.keyword || '');
    const [searchCalendar, setSearchCalendar] = useState<CalendarOption>({
        value: '',
        label: '',
    });
    // 表示用色オプション（並び順・仕切り付き）
    const [colorOptionsView, setColorOptionsView] = useState<ColorOption[]>([]);
    // プランロック用モーダル
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    /** 分析ページ・スマホ時の絞り込み（色・キーワード）アコーディオン */
    const [isAnalysisFilterExpanded, setIsAnalysisFilterExpanded] = useState(false);

    // 個別カレンダーリストからオプションを作成（祝日カレンダー除外）
    // 有料プランでない場合はメインカレンダー以外をロック
    const calendarOptions = useMemo(
        () => createIndividualCalendarOptions(calendarList, { lockNonDefault: !isPaidPlan }),
        [calendarList, isPaidPlan],
    );

    // カレンダー選択のドロップダウン幅を計算（最長の項目に合わせる）
    const [calendarDropdownWidth, setCalendarDropdownWidth] = useState<React.CSSProperties>({});

    // 実際のDOM要素で幅を測定
    useEffect(() => {
        if (!calendarOptions || calendarOptions.length === 0) {
            setCalendarDropdownWidth({});
            return;
        }

        // 一時的な要素を作成して幅を測定（最大幅の制限はかけない）
        const measureElement = document.createElement('div');
        measureElement.style.position = 'absolute';
        measureElement.style.visibility = 'hidden';
        measureElement.style.whiteSpace = 'nowrap';
        measureElement.style.fontSize = '0.875rem'; // text-baseと同じ
        measureElement.style.fontFamily = getComputedStyle(document.body).fontFamily;
        measureElement.style.padding = '8px 16px'; // px-4 py-2と同じ
        document.body.appendChild(measureElement);

        let maxWidth = 0;
        calendarOptions.forEach((opt) => {
            // ロックアイコンがある場合はテキストの後に追加
            const text = opt.isLocked ? `${opt.label} 🔒` : opt.label;
            measureElement.textContent = text;
            const width = measureElement.offsetWidth;
            if (width > maxWidth) {
                maxWidth = width;
            }
        });

        document.body.removeChild(measureElement);

        // 計測した最長幅をそのまま使用（最大値の制限なし）
        setCalendarDropdownWidth({
            width: `${maxWidth}px`,
        });
    }, [calendarOptions]);

    // 祝日・誕生日カレンダーを除外したカレンダーリスト
    const filteredCalendarList = useMemo(() => {
        return calendarList?.filter((cal) => !isHolidayOrBirthdayCalendar(cal)) || [];
    }, [calendarList]);

    // カレンダーが1つだけかどうか
    const isSingleCalendar = filteredCalendarList.length === 1;

    // デフォルトカレンダー（is_default=true）を取得
    const defaultCalendarOption = useMemo(
        () => getDefaultIndividualCalendarOption(calendarList),
        [calendarList],
    );

    const defaultColorOption = useMemo(
        () => localizeColorOption(getDefaultColorOption()),
        [localizeColorOption],
    );

    // 色選択
    const [searchColor, setSearchColor] = useState<ColorOption>(() =>
        localizeColorOption(initialSearchParams?.color || getDefaultColorOption()),
    );

    const [searchPeriod, setSearchPeriod] = useState<{ startDate: Date | null; endDate: Date | null }>(() =>
        getInitialDate(initialSearchParams, { weekStartsOn: weekStart }),
    );

    // 集計単位の状態
    const [bucket, setBucket] = useState<'day' | 'week' | 'month'>(initialSearchParams?.bucket || 'day');

    // 検索期間のISO文字列を取得
    const startDate: string = searchPeriod?.startDate
        ? formatDateForServer(searchPeriod.startDate)
        : '';
    const endDate: string = searchPeriod?.endDate ? formatDateForServer(searchPeriod.endDate) : '';
    const [isFirstSearch, setIsFirstSearch] = useState(true);

    // Datepicker の入力要素への参照（iOSのキーボード抑止用）
    const datepickerContainerRef = useRef<HTMLSpanElement>(null);

    // iOSでのキーボード表示を抑止（readonly + inputmode=none を付与）
    useEffect(() => {
        const container = datepickerContainerRef.current;
        if (!container) return;
        const input = container.querySelector('input');
        if (!input) return;
        input.setAttribute('readonly', 'true');
        input.setAttribute('inputmode', 'none');
        input.setAttribute('autocomplete', 'off');
    }, []);

    /**
     * カレンダー選択のラベルをロケールに合わせて更新
     */
    useEffect(() => {
        if (!calendarOptions.length) return;
        setSearchCalendar((prev) => {
            if (!prev.value) {
                return defaultCalendarOption;
            }
            const matched = calendarOptions.find((option) => option.value === prev.value);
            return matched ?? prev;
        });
    }, [calendarOptions, defaultCalendarOption]);

    /**
     * 初期値設定（前回の検索パラメータを復元）
     */
    useEffect(() => {
        if (initialSearchParams) {
            // キーワードがある場合は設定
            if (initialSearchParams.keyword) {
                setSearchKeyword(initialSearchParams.keyword);
            }

            // 日付範囲がある場合は設定
            if (initialSearchParams.startDate && initialSearchParams.endDate) {
                setSearchPeriod({
                    startDate: new Date(initialSearchParams.startDate),
                    endDate: new Date(initialSearchParams.endDate),
                });
                setIsFirstSearch(false);
            }

            // 色選択（初期値がある場合は設定）
            if (initialSearchParams.color) {
                setSearchColor(localizeColorOption(initialSearchParams.color));
            } else {
                setSearchColor(defaultColorOption);
            }

            // 集計単位（初期値がある場合は設定）
            if (initialSearchParams.bucket) {
                setBucket(initialSearchParams.bucket);
            }

            // カレンダー選択（初期値がある場合は設定）
            if (initialSearchParams.calendar) {
                setSearchCalendar(initialSearchParams.calendar);
            }
        }
    }, [defaultColorOption, initialSearchParams, localizeColorOption]);

    useEffect(() => {
        if (initialSearchParams?.startDate && initialSearchParams?.endDate) {
            return;
        }
        if (!isFirstSearch) {
            return;
        }
        setSearchPeriod(getInitialDate(undefined, { weekStartsOn: weekStart }));
    }, [initialSearchParams, isFirstSearch, weekStart]);

    /**
     * 色オプションの描画順を更新
     */
    const refreshColorOptions = useCallback(
        async (calendarId: string) => {
            // 個別カレンダーID（user_calendarsテーブルのid）からカレンダー情報を取得
            const selected = calendarList?.find((c) => String(c.id) === calendarId);
            const providerName = selected?.providerName || '';
            const base = await getBaseColorOptionsByProvider(providerName, languageTag);
            const userIdValue = userInfo?.userId || '';
            // LocalStorageのキーはカレンダーIDベースに変更
            const key = `${LocalStorageKeys.COLOR_ORDER}:${calendarId}`;
            let order: string[] = [];
            const saved = await getLocalStorage(userIdValue, key);
            if (saved) {
                try {
                    order = JSON.parse(saved);
                } catch {
                    order = [];
                }
            }
            const ordered = applyColorOrderWithDividers(base, order).map(localizeColorOption);
            setColorOptionsView(ordered);
        },
        [calendarList, localizeColorOption, userInfo?.userId, languageTag],
    );

    // カレンダー変更時に色リストを並べ替え
    useEffect(() => {
        if (searchCalendar?.value) {
            refreshColorOptions(searchCalendar.value);
        }
    }, [refreshColorOptions, searchCalendar]);

    useEffect(() => {
        setColorOptionsView((prev) => prev.map((option) => localizeColorOption(option)));
        setSearchColor((prev) => localizeColorOption(prev));
    }, [localizeColorOption]);

    const selectedCalendarOption = useMemo(() => {
        const match = calendarOptions.find((option) => option.value === searchCalendar.value);
        if (match) return match;
        if (searchCalendar.value) return searchCalendar;
        return defaultCalendarOption;
    }, [calendarOptions, defaultCalendarOption, searchCalendar]);

    const selectedColorOption = useMemo(() => {
        const match = colorOptionsView.find((option) => option.value === searchColor.value);
        if (match) return match;
        if (searchColor) return searchColor;
        return defaultColorOption;
    }, [colorOptionsView, defaultColorOption, searchColor]);

    const [isPeriodLockedModalOpen, setIsPeriodLockedModalOpen] = useState(false);
    const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);

    /**
     * 更新ボタン押下 / 自動反映
     */
    const handleSubmit = useCallback(() => {
        const isAllColor = searchColor?.value === 'all';
        const effectiveKeyword = isAllColor ? '' : searchKeyword;

        const calendarToUse = isSingleCalendar && filteredCalendarList[0]
            ? { value: String(filteredCalendarList[0].id), label: filteredCalendarList[0].calendarName }
            : searchCalendar;

        handleSearch?.(
            calendarToUse,
            startDate,
            endDate,
            effectiveKeyword,
            searchColor,
            bucket,
        );
    }, [
        handleSearch,
        searchColor,
        searchKeyword,
        isSingleCalendar,
        filteredCalendarList,
        searchCalendar,
        startDate,
        endDate,
        bucket,
    ]);

    useEffect(() => {
        if (!hideSubmitButton || !handleSearch) return;
        if (!isSingleCalendar && !searchCalendar.value) return;
        if (!startDate || !endDate) return;
        const id = window.setTimeout(() => {
            handleSubmit();
        }, 300);
        return () => window.clearTimeout(id);
    }, [
        hideSubmitButton,
        handleSearch,
        handleSubmit,
        isSingleCalendar,
        searchCalendar.value,
        startDate,
        endDate,
        searchKeyword,
        searchColor.value,
        bucket,
    ]);

    /**
     * レンダリング
     */
    return (
        <Fragment>
            {analysisToolbarMiddle != null ? (
                <div className="relative flex w-full flex-col px-4 pt-12 sm:px-6 lg:px-8">
                    {!isSingleCalendar && (
                        <div className="absolute top-0 left-1/2 -translate-x-1/2">
                            <button
                                type="button"
                                onClick={() => setIsCalendarExpanded((v) => !v)}
                                className="flex h-7 items-center justify-center gap-1.5 rounded-b-md border-b border-l border-r border-gray-600 bg-transparent px-3 text-gray-700 transition-colors hover:bg-gray-50"
                            >
                                <svg
                                    className={`h-4 w-4 transition-transform ${isCalendarExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                                <span className="text-xs">{t('calendar.choose')}</span>
                            </button>
                        </div>
                    )}
                    {!isSingleCalendar && (
                        <div
                            className={`relative z-20 w-full transition-all duration-300 ${
                                isCalendarExpanded ? 'max-h-40 opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
                            }`}
                        >
                            <section className="mb-5">
                                <div className="flex flex-wrap items-center">
                                    <p className="text-gray-500 mb-0 mr-2 shrink-0 pb-1 min-w-[100px] lg:min-w-[10px] lg:mb-0">
                                        {t('labels.calendar')}
                                    </p>
                                    <Select
                                        options={calendarOptions}
                                        defaultValue={selectedCalendarOption}
                                        onValueChange={(option) => {
                                            if (option.isLocked) {
                                                setIsPlanModalOpen(true);
                                                return;
                                            }
                                            setSearchCalendar(option);
                                            setSearchColor(defaultColorOption);
                                        }}
                                        className="min-w-[250px] w-full text-base"
                                        dropdownStyle={calendarDropdownWidth}
                                        noWrap={true}
                                    />
                                </div>
                            </section>
                        </div>
                    )}
                    <section
                        className={`w-full max-w-full ${
                            !isSingleCalendar && isCalendarExpanded ? 'border-t border-gray-100 pt-4' : ''
                        }`}
                    >
                        {analysisToolbarMiddle}
                    </section>
                    <section className="mt-5 w-full max-w-full sm:flex sm:justify-center">
                        <div className="w-full max-w-full rounded-xl bg-gray-100 sm:flex sm:w-auto sm:flex-row sm:items-center sm:gap-6 sm:px-6 sm:py-3">
                            <button
                                type="button"
                                className="flex w-full items-center gap-2 px-4 py-3 text-left text-gray-600 sm:hidden"
                                aria-expanded={isAnalysisFilterExpanded}
                                aria-controls="analysis-search-filter-panel"
                                onClick={() => setIsAnalysisFilterExpanded((v) => !v)}
                            >
                                <FilterIcon className="size-4 shrink-0" />
                                <span className="text-sm">{t('labels.filter')}</span>
                                <svg
                                    className={`ms-auto h-5 w-5 shrink-0 text-gray-500 transition-transform ${
                                        isAnalysisFilterExpanded ? 'rotate-180' : ''
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </button>
                            <div
                                id="analysis-search-filter-panel"
                                className={`max-sm:text-left sm:flex sm:flex-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6 sm:px-0 sm:py-0 ${
                                    isAnalysisFilterExpanded
                                        ? 'flex flex-col items-stretch gap-4 px-4 pb-4'
                                        : 'hidden sm:flex'
                                }`}
                            >
                                <div className="hidden items-center gap-1.5 text-gray-600 sm:flex">
                                    <FilterIcon className="size-4" />
                                    <span className="text-sm">{t('labels.filter')}</span>
                                </div>
                                <div className="hidden h-6 w-px bg-gray-300 sm:block"></div>
                                <div className="flex w-full min-w-0 flex-col items-stretch gap-1 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                                    <p className="mb-0 w-full shrink-0 text-left text-sm text-gray-500 sm:mb-0 sm:mr-2 sm:w-auto sm:min-w-[100px] sm:pb-1 sm:text-start lg:min-w-[10px] lg:mb-0">
                                        {t('labels.color')}
                                    </p>
                                    <Select
                                        options={colorOptionsView}
                                        defaultValue={selectedColorOption}
                                        onValueChange={(option) => {
                                            if ((option as any).isDivider) return;
                                            setSearchColor(localizeColorOption(option));
                                            const v = option?.value;
                                            if (v && v !== 'all') {
                                                (async () => {
                                                    const userId = userInfo?.userId || '';
                                                    const key = `${LocalStorageKeys.COLOR_ORDER}:${searchCalendar.value}`;
                                                    const saved = await getLocalStorage(userId, key);
                                                    let order: string[] = [];
                                                    if (saved) {
                                                        try {
                                                            order = JSON.parse(saved);
                                                        } catch {}
                                                    }
                                                    order = [v, ...order.filter((x) => x !== v)].slice(0, 20);
                                                    await setLocalStorage(userId, key, JSON.stringify(order));
                                                    await refreshColorOptions(searchCalendar.value);
                                                })();
                                            }
                                        }}
                                        className="w-full min-w-0 text-base sm:w-44 sm:min-w-0"
                                        dropdownClassName="min-w-full !w-max max-w-[min(100vw-2rem,22rem)]"
                                    />
                                </div>
                                <div className="flex w-full min-w-0 flex-col items-stretch gap-1 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                                    <span className="mb-0 w-full shrink-0 text-left text-sm text-gray-500 sm:mb-0 sm:mr-2 sm:w-auto sm:min-w-[150px] sm:pb-1 sm:text-start lg:min-w-[10px] lg:mb-0">
                                        {t('labels.keyword')}
                                    </span>
                                    <span className="block w-full min-w-0 sm:w-auto">
                                        {(() => {
                                            const isAllColor = searchColor?.value === 'all';
                                            return (
                                                <Input
                                                    className="w-full rounded-lg border-2 border-lightest-gray focus-visible:border-white sm:w-auto"
                                                    placeholder={
                                                        isAllColor
                                                            ? t('placeholders.keywordDisabled')
                                                            : t('placeholders.keywordExample')
                                                    }
                                                    error={false}
                                                    helperText=""
                                                    showClearButton={!isAllColor}
                                                    name="searchKeyword"
                                                    value={searchKeyword}
                                                    disabled={isAllColor}
                                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (isAllColor) return;
                                                        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                                            handleSubmit();
                                                        }
                                                    }}
                                                />
                                            );
                                        })()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            ) : (
                <div className="flex w-full flex-col items-center gap-6 px-4 lg:flex-row lg:items-center">
                    {/* 左側: 入力群（折り返し可） */}
                    <div className="flex w-full min-w-0 flex-1 flex-wrap items-center gap-6">
                        {/* カレンダー選択 */}
                        {/* 祝日カレンダーを除外してカレンダーが1つだけの場合は非表示 */}
                        {!isSingleCalendar && (
                            <div className="flex flex-wrap items-center">
                                <p className="text-gray-500 mb-0 mr-2 shrink-0 pb-1 min-w-[100px] lg:min-w-[10px] lg:mb-0">
                                    {t('labels.calendar')}
                                </p>
                                <Select
                                    options={calendarOptions}
                                    defaultValue={selectedCalendarOption}
                                    onValueChange={(option) => {
                                        if (option.isLocked) {
                                            setIsPlanModalOpen(true);
                                            return;
                                        }
                                        setSearchCalendar(option);
                                        setSearchColor(defaultColorOption);
                                    }}
                                    className="min-w-[250px] w-full text-base"
                                    dropdownStyle={calendarDropdownWidth}
                                    noWrap={true}
                                />
                            </div>
                        )}
                        {!hidePeriod && (
                            <div className="flex flex-wrap items-center">
                                <span className="text-gray-500 mb-0 mr-2 shrink-0 pb-1 min-w-[100px] lg:min-w-[10px] lg:mb-0">
                                    {t('labels.period')}
                                </span>
                                <span ref={datepickerContainerRef} className="w-full max-w-[260px]">
                                    <CustomDateRangePicker
                                        startDate={searchPeriod.startDate}
                                        endDate={searchPeriod.endDate}
                                        bucket={bucket}
                                        onChange={(start, end, newBucket) => {
                                            setSearchPeriod({ startDate: start, endDate: end });
                                            setBucket(newBucket);
                                        }}
                                        weekStartsOn={weekStart}
                                    />
                                </span>
                            </div>
                        )}
                        <div className="flex flex-wrap items-center">
                            <p className="text-gray-500 mb-0 mr-2 shrink-0 pb-1 min-w-[100px] lg:min-w-[10px] lg:mb-0">
                                {t('labels.color')}
                            </p>
                            <Select
                                options={colorOptionsView}
                                defaultValue={selectedColorOption}
                                onValueChange={(option) => {
                                    if ((option as any).isDivider) return;
                                    setSearchColor(localizeColorOption(option));
                                    const v = option?.value;
                                    if (v && v !== 'all') {
                                        (async () => {
                                            const userId = userInfo?.userId || '';
                                            const key = `${LocalStorageKeys.COLOR_ORDER}:${searchCalendar.value}`;
                                            const saved = await getLocalStorage(userId, key);
                                            let order: string[] = [];
                                            if (saved) {
                                                try {
                                                    order = JSON.parse(saved);
                                                } catch {}
                                            }
                                            order = [v, ...order.filter((x) => x !== v)].slice(0, 20);
                                            await setLocalStorage(userId, key, JSON.stringify(order));
                                            await refreshColorOptions(searchCalendar.value);
                                        })();
                                    }
                                }}
                                className="w-full min-w-0 text-base sm:w-44"
                                dropdownClassName="min-w-full !w-max max-w-[min(100vw-2rem,22rem)]"
                            />
                        </div>
                        <div className="flex flex-wrap items-center">
                            <span className="text-gray-500 mb-0 mr-2 shrink-0 pb-1 min-w-[150px] lg:min-w-[10px] lg:mb-0">
                                {t('labels.keyword')}
                            </span>
                            <span>
                                {(() => {
                                    const isAllColor = searchColor?.value === 'all';
                                    return (
                                        <Input
                                            className="rounded-lg border-2 border-lightest-gray focus-visible:border-white"
                                            placeholder={
                                                isAllColor
                                                    ? t('placeholders.keywordDisabled')
                                                    : t('placeholders.keywordExample')
                                            }
                                            error={false}
                                            helperText=""
                                            showClearButton={!isAllColor}
                                            name="searchKeyword"
                                            value={searchKeyword}
                                            disabled={isAllColor}
                                            onChange={(e) => setSearchKeyword(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (isAllColor) return;
                                                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                                    handleSubmit();
                                                }
                                            }}
                                        />
                                    );
                                })()}
                            </span>
                        </div>
                    </div>
                    {!hideSubmitButton && (
                        <div className="mt-2 flex w-full shrink-0 items-center justify-center lg:mt-0 lg:w-auto lg:min-w-[180px] lg:basis-1/5">
                            <Button
                                variant="original"
                                onClick={handleSubmit}
                                className={`w-auto gap-1 px-6 ${isFirstSearch && !submitButtonLabel ? 'animate-beat hover:paused' : ''}`}
                            >
                                <span className="text-md pb-0.5 tracking-[0.1em]">
                                    {submitButtonLabel ?? t('button.analyze')}
                                </span>
                                {!submitButtonLabel && (
                                    <img
                                        src={magnifyingGlass}
                                        alt={t('button.analyzeAlt')}
                                        className="ml-1 w-4"
                                    />
                                )}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {!isPaidPlan && (
                <PlanLockedModal
                    isOpen={isPlanModalOpen}
                    onClose={() => setIsPlanModalOpen(false)}
                    onGoPlans={() =>
                        openPlansWithTracking({
                            source: 'calendar_selection_analysis',
                        })
                    }
                />
            )}
            {/* 期間選択ロックモーダル */}
            <PeriodLockedModal
                isOpen={isPeriodLockedModalOpen}
                onClose={() => setIsPeriodLockedModalOpen(false)}
                onGoPlans={() => {
                    setIsPeriodLockedModalOpen(false);
                    openPlansWithTracking({
                        source: 'period_selection_modal',
                        panelId: 'anSearch',
                    });
                }}
            />
        </Fragment>
    );
};

export default Search;
